import React from "react";
import {
  Card,
  CardContent,
  Box,
  Typography,
  Avatar,
  Chip,
  LinearProgress,
  Tooltip,
  useTheme,
  alpha,
} from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

type ColorVariant = "primary" | "info" | "success" | "warning" | "error";

interface KPICardProps {
  title: string;
  value: string | number;
  unit?: string;
  icon: React.ReactNode;
  color: ColorVariant;
  description?: string;
  trend?: {
    value: string;
    label: string;
    direction: "up" | "down";
  };
  progress?: {
    value: number;
    target?: string;
  };
}

export const KPICard: React.FC<KPICardProps> = ({
  title,
  value,
  unit,
  icon,
  color,
  description,
  trend,
  progress,
}) => {
  const theme = useTheme();

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 3,
        height: "100%",
        transition: "all 0.2s ease-in-out",
        "&:hover": {
          boxShadow: 4,
          borderColor: `${color}.main`,
        },
      }}
    >
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={500}>
                {title}
              </Typography>
              {description && (
                <Tooltip title={description} arrow placement="top">
                  <InfoOutlinedIcon
                    sx={{
                      fontSize: 14,
                      color: "text.disabled",
                      cursor: "help",
                      "&:hover": { color: "text.secondary" },
                    }}
                  />
                </Tooltip>
              )}
            </Box>
            <Typography variant="h4" fontWeight={700} sx={{ mt: 0.5 }}>
              {value}
              {unit && (
                <Typography
                  component="span"
                  variant="body1"
                  color="text.secondary"
                  sx={{ ml: 0.5 }}
                >
                  {unit}
                </Typography>
              )}
            </Typography>
          </Box>
          <Avatar
            sx={{
              bgcolor: alpha(theme.palette[color].main, 0.1),
              color: `${color}.main`,
            }}
          >
            {icon}
          </Avatar>
        </Box>

        {trend && (
          <Box sx={{ display: "flex", alignItems: "center", mt: 1.5, gap: 0.5 }}>
            <Chip
              icon={
                trend.direction === "up" ? (
                  <ArrowUpwardIcon sx={{ fontSize: 14 }} />
                ) : (
                  <ArrowDownwardIcon sx={{ fontSize: 14 }} />
                )
              }
              label={trend.value}
              size="small"
              sx={{
                bgcolor: alpha(
                  trend.direction === "up"
                    ? theme.palette.success.main
                    : theme.palette.error.main,
                  0.1
                ),
                color: trend.direction === "up" ? "success.main" : "error.main",
                fontWeight: 600,
                fontSize: 11,
                height: 22,
              }}
            />
            <Typography variant="caption" color="text.secondary">
              {trend.label}
            </Typography>
          </Box>
        )}

        {progress && (
          <Box sx={{ mt: 1.5 }}>
            <LinearProgress
              variant="determinate"
              value={progress.value}
              sx={{
                height: 6,
                borderRadius: 3,
                bgcolor: alpha(theme.palette[color].main, 0.1),
                "& .MuiLinearProgress-bar": {
                  bgcolor: `${color}.main`,
                  borderRadius: 3,
                },
              }}
            />
            {progress.target && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.5, display: "block" }}
              >
                {progress.target}
              </Typography>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
